import { logger } from '@/shared/utils/logger';

export type LocationPermissionState = 'granted' | 'denied' | 'prompt' | 'unknown';

export interface LocationCoordinates {
  latitude: number;
  longitude: number;
  accuracy?: number;
  timestamp: number;
}

export interface LocationError {
  code: 'PERMISSION_DENIED' | 'POSITION_UNAVAILABLE' | 'TIMEOUT' | 'NOT_SUPPORTED' | 'UNKNOWN';
  message: string;
}

// Cache duration in milliseconds (5 minutes)
const LOCATION_CACHE_DURATION = 5 * 60 * 1000;

// Storage keys
const STORAGE_KEYS = {
  CACHED_LOCATION: 'donvio_cached_location',
  PERMISSION_STATE: 'donvio_location_permission',
  DONT_ASK_AGAIN: 'donvio_location_dont_ask',
  LAST_DENIAL_DATE: 'donvio_location_denial_date',
};

class LocationService {
  private cachedLocation: LocationCoordinates | null = null;

  constructor() {
    this.loadCachedLocation();
  }

  /**
   * Checks the current location permission state
   */
  async checkPermissionState(): Promise<LocationPermissionState> {
    try {
      if (!('permissions' in navigator)) {
        return 'unknown';
      }

      const permission = await navigator.permissions.query({ name: 'geolocation' });
      const state = permission.state as LocationPermissionState;
      
      // Cache the permission state
      localStorage.setItem(STORAGE_KEYS.PERMISSION_STATE, state);
      
      // Track denial date if denied
      if (state === 'denied') {
        const lastDenial = localStorage.getItem(STORAGE_KEYS.LAST_DENIAL_DATE);
        if (!lastDenial) {
          localStorage.setItem(STORAGE_KEYS.LAST_DENIAL_DATE, Date.now().toString());
        }
      }

      return state;
    } catch (error) {
      // Permissions API can throw on older browsers / privacy-locked contexts;
      // not actionable, surface as warn so it doesn't reach Sentry as an error.
      logger.warn('Error checking location permission:', error);
      return 'unknown';
    }
  }

  /**
   * Gets the user's current location with caching
   */
  async getCurrentLocation(options?: {
    forceRefresh?: boolean;
    silent?: boolean;
    timeout?: number;
  }): Promise<LocationCoordinates> {
    const { forceRefresh = false, silent = false, timeout = 45000 } = options || {};

    // Check cached location first
    if (!forceRefresh && this.cachedLocation && this.isCacheValid()) {
      logger.info('Returning cached location');
      return this.cachedLocation;
    }

    // Check permission state if silent mode
    if (silent) {
      const permissionState = await this.checkPermissionState();
      if (permissionState === 'denied') {
        throw this.createError('PERMISSION_DENIED', 'Location permission was previously denied');
      }
    }

    // Check if geolocation is supported
    if (!navigator.geolocation) {
      throw this.createError('NOT_SUPPORTED', 'Geolocation is not supported by your browser');
    }

    // Check network connectivity
    if (!navigator.onLine) {
      logger.warn('Device appears to be offline');
      // Try to return cached location if available
      if (this.cachedLocation) {
        logger.info('Returning expired cached location due to offline status');
        return this.cachedLocation;
      }
      throw this.createError('POSITION_UNAVAILABLE', 'Location services require an internet connection. Please check your network settings.');
    }

    // Try two-stage approach: first quick with lower accuracy, then high accuracy
    try {
      // First attempt: Quick response with moderate accuracy
      logger.info('Attempting quick location request...');
      return await this.getLocationWithOptions({
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 30000, // Accept cached position up to 30 seconds old
      });
    } catch {
      logger.info('Quick location failed, trying high accuracy...');
      
      // Second attempt: High accuracy with longer timeout
      try {
        return await this.getLocationWithOptions({
          enableHighAccuracy: true,
          timeout,
          maximumAge: 60000, // Accept cached position up to 1 minute old
        });
      } catch (secondError) {
        const error = secondError as LocationError;

        // If position is unavailable or timed out and we have cached location, use it
        if ((error.code === 'POSITION_UNAVAILABLE' || error.code === 'TIMEOUT') && this.cachedLocation) {
          logger.warn(`${error.code === 'TIMEOUT' ? 'Location request timed out' : 'Position unavailable'}, returning expired cached location`);
          return {
            ...this.cachedLocation,
            accuracy: (this.cachedLocation.accuracy || 0) + 1000, // Indicate reduced accuracy
          };
        }

        // Browser geolocation failures are expected user/device states (denied,
        // GPS unavailable, timeout). Surface them as warn so Sentry doesn't
        // treat them as application errors.
        if (error.code === 'TIMEOUT') {
          logger.warn('Location request timed out without cached fallback');
        } else if (error.code !== 'PERMISSION_DENIED') {
          logger.warn('Both location attempts failed:', secondError);
        }
        throw secondError;
      }
    }
  }

  /**
   * Helper method to get location with specific options
   */
  private getLocationWithOptions(options: PositionOptions): Promise<LocationCoordinates> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location: LocationCoordinates = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: Date.now(),
          };

          this.cacheLocation(location);
          resolve(location);
        },
        (error) => {
          // GeolocationPositionError is always an expected user/device state
          // (permission, timeout, position unavailable). Never log as error
          // — it would otherwise reach Sentry as application noise.
          if (error.code === error.PERMISSION_DENIED) {
            logger.info('User denied location permission');
          } else if (error.code === error.TIMEOUT) {
            logger.warn('Geolocation timeout:', error.message);
          } else {
            logger.warn('Geolocation position unavailable:', error.message);
          }
          reject(this.handleGeolocationError(error));
        },
        options
      );
    });
  }

  /**
   * Gets cached location without prompting for permission
   */
  getCachedLocation(): LocationCoordinates | null {
    if (this.cachedLocation && this.isCacheValid()) {
      return this.cachedLocation;
    }
    return null;
  }

  /**
   * Gets any cached location, even if expired
   */
  getAnyCachedLocation(): LocationCoordinates | null {
    return this.cachedLocation;
  }

  /**
   * Checks if user has set "don't ask again" preference
   */
  shouldAskForLocation(): boolean {
    const dontAskAgain = localStorage.getItem(STORAGE_KEYS.DONT_ASK_AGAIN);
    if (dontAskAgain === 'true') {
      return false;
    }

    // Check if enough time has passed since last denial (7 days)
    const lastDenialStr = localStorage.getItem(STORAGE_KEYS.LAST_DENIAL_DATE);
    if (lastDenialStr) {
      const lastDenial = parseInt(lastDenialStr, 10);
      const daysSinceDenial = (Date.now() - lastDenial) / (1000 * 60 * 60 * 24);
      return daysSinceDenial >= 7;
    }

    return true;
  }

  /**
   * Sets user preference to not ask for location again
   */
  setDontAskAgain(value: boolean): void {
    localStorage.setItem(STORAGE_KEYS.DONT_ASK_AGAIN, value.toString());
  }

  /**
   * Detects the user's browser for help instructions
   */
  detectBrowser(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
      return 'chrome';
    } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
      return 'safari';
    } else if (userAgent.includes('firefox')) {
      return 'firefox';
    } else if (userAgent.includes('edg')) {
      return 'edge';
    }
    
    return 'unknown';
  }

  /**
   * Gets browser-specific help instructions
   */
  getHelpInstructions(): { browser: string; steps: string[] } {
    const browser = this.detectBrowser();
    const isMobile = /mobile|android|iphone|ipad/i.test(navigator.userAgent);

    const instructions: Record<string, string[]> = {
      chrome: isMobile
        ? [
            'Tap the menu icon (three dots) in the top right',
            'Go to Settings > Site settings > Location',
            'Find donvio.com and tap it',
            'Change permission to "Allow"',
            'Refresh the page'
          ]
        : [
            'Click the lock icon in the address bar',
            'Find "Location" in the permissions list',
            'Change it from "Block" to "Allow"',
            'Refresh the page'
          ],
      safari: isMobile
        ? [
            'Go to Settings app > Safari',
            'Tap "Location" under "Settings for Websites"',
            'Select "Ask" or "Allow"',
            'Return to Donvio and refresh'
          ]
        : [
            'Go to Safari > Preferences > Websites',
            'Click "Location" in the sidebar',
            'Find donvio.com and change to "Allow"',
            'Refresh the page'
          ],
      firefox: [
        'Click the lock icon in the address bar',
        'Click the arrow next to "Blocked" permissions',
        'Click the X next to "Location" to remove the block',
        'Refresh the page'
      ],
      edge: [
        'Click the lock icon in the address bar',
        'Click "Permissions for this site"',
        'Find "Location" and change to "Allow"',
        'Refresh the page'
      ],
      unknown: [
        'Look for a lock or info icon in your browser\'s address bar',
        'Find location or site permissions',
        'Allow location access for donvio.com',
        'Refresh the page'
      ],
    };

    return {
      browser: browser === 'unknown' ? 'your browser' : browser,
      steps: instructions[browser] || instructions.unknown,
    };
  }

  /**
   * Clears cached location data
   */
  clearCache(): void {
    this.cachedLocation = null;
    localStorage.removeItem(STORAGE_KEYS.CACHED_LOCATION);
  }

  private loadCachedLocation(): void {
    try {
      const cached = localStorage.getItem(STORAGE_KEYS.CACHED_LOCATION);
      if (cached) {
        this.cachedLocation = JSON.parse(cached);
      }
    } catch (error) {
      // localStorage failure / corrupt JSON — non-actionable, fall back to no cache.
      logger.warn('Error loading cached location:', error);
    }
  }

  private cacheLocation(location: LocationCoordinates): void {
    this.cachedLocation = location;
    try {
      localStorage.setItem(STORAGE_KEYS.CACHED_LOCATION, JSON.stringify(location));
    } catch (error) {
      // Storage quota / private mode — non-actionable, in-memory cache still works.
      logger.warn('Error caching location:', error);
    }
  }

  private isCacheValid(): boolean {
    if (!this.cachedLocation) return false;
    return Date.now() - this.cachedLocation.timestamp < LOCATION_CACHE_DURATION;
  }

  private handleGeolocationError(error: GeolocationPositionError): LocationError {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        // Update permission state
        localStorage.setItem(STORAGE_KEYS.PERMISSION_STATE, 'denied');
        localStorage.setItem(STORAGE_KEYS.LAST_DENIAL_DATE, Date.now().toString());
        return this.createError('PERMISSION_DENIED', 'Location permission was denied');
      
      case error.POSITION_UNAVAILABLE:
        return this.createError('POSITION_UNAVAILABLE', 'Unable to determine your location. Please ensure:\n• Location Services are enabled in your device settings\n• You have a stable internet connection\n• GPS has a clear view (if indoors, try moving near a window)');
      
      case error.TIMEOUT:
        return this.createError('TIMEOUT', 'Having trouble getting your location. This usually happens when:\n• Location Services are disabled on your device\n• You\'re indoors with poor GPS signal (try moving near a window)\n• Your device is in battery saving mode\n\nPlease try again or check your device settings.');
      
      default:
        return this.createError('UNKNOWN', 'An unknown error occurred');
    }
  }

  private createError(code: LocationError['code'], message: string): LocationError {
    return { code, message };
  }
}

// Export singleton instance
export const locationService = new LocationService();